/**
 * JWT Helper Utilities
 * 
 * Production-ready JWT token decoding with proper error handling.
 * Centralizes all JWT operations to prevent decoding errors.
 */

/**
 * Safely decode a JWT token payload
 * Handles base64 padding and malformed tokens
 * 
 * @param token - JWT token string
 * @returns Decoded payload object or null if invalid
 */
export function safeDecodeJWT(token: string): Record<string, any> | null {
  try {
    // Validate token exists and is not empty
    if (!token || typeof token !== 'string' || token.trim() === '') {
      console.warn('JWT decode: Empty or invalid token provided');
      return null;
    }

    // Handle mock tokens for development (non-JWT format)
    const USEMOCK_AUTH = import.meta.env.VITE_USE_MOCK_AUTH === 'true';
    if (USEMOCK_AUTH && !token.includes('.')) {
      try {
        return JSON.parse(atob(token));
      } catch (mockError) {
        console.warn('JWT decode: Failed to decode mock token');
        return null;
      }
    }

    // Split JWT into parts (header.payload.signature)
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.warn(`JWT decode: Invalid token format, expected 3 parts but got ${parts.length}`);
      return null;
    }

    // Extract and decode payload (second part)
    const payload = parts[1];
    
    // Add padding if needed for base64 decoding
    // Base64 strings must be a multiple of 4 characters
    const paddedPayload = payload + '='.repeat((4 - (payload.length % 4)) % 4);
    
    // Decode base64 and parse JSON
    const decodedString = atob(paddedPayload);
    const payloadObject = JSON.parse(decodedString);
    
    return payloadObject;
  } catch (error) {
    console.error('JWT decode: Error decoding token', error);
    return null;
  }
}

/**
 * Extract tenant ID from JWT token
 * 
 * @param token - JWT token string
 * @returns Tenant ID or null if not found
 */
export function getTenantFromJWT(token: string): string | null {
  const payload = safeDecodeJWT(token);
  if (!payload) {
    return null;
  }
  
  // Check multiple possible tenant claim names
  return payload.tenant || payload.tenant_id || payload.tenantId || null;
}

/**
 * Extract user roles from JWT token
 * 
 * @param token - JWT token string
 * @returns Array of role strings or empty array if not found
 */
export function getRolesFromJWT(token: string): string[] {
  const payload = safeDecodeJWT(token);
  if (!payload) {
    return [];
  }
  
  // Ensure roles is always an array
  if (Array.isArray(payload.roles)) {
    return payload.roles;
  }
  
  // Handle single role as string
  if (typeof payload.roles === 'string') {
    return [payload.roles];
  }
  
  return [];
}

/**
 * Extract user ID from JWT token
 * 
 * @param token - JWT token string
 * @returns User ID or null if not found
 */
export function getUserIdFromJWT(token: string): string | null {
  const payload = safeDecodeJWT(token);
  if (!payload) {
    return null;
  }
  
  // Check multiple possible user ID claim names
  return payload.sub || payload.user_id || payload.userId || null;
}

/**
 * Check if JWT token is expired
 * 
 * @param token - JWT token string
 * @param toleranceSeconds - Clock skew tolerance in seconds (default: 60)
 * @returns True if token is expired, false otherwise
 */
export function isJWTExpired(token: string, toleranceSeconds: number = 60): boolean {
  const payload = safeDecodeJWT(token);
  if (!payload) {
    return true; // Consider invalid tokens as expired
  }
  
  // Check if exp claim exists
  if (!payload.exp || typeof payload.exp !== 'number') {
    console.warn('JWT: Token missing expiration claim');
    return true;
  }
  
  // Get current time in seconds
  const currentTime = Math.floor(Date.now() / 1000);
  
  // Check if token is expired (with tolerance for clock skew)
  return currentTime > (payload.exp + toleranceSeconds);
}

/**
 * Get token expiration time as Date object
 * 
 * @param token - JWT token string
 * @returns Date object or null if no expiration
 */
export function getJWTExpiration(token: string): Date | null {
  const payload = safeDecodeJWT(token);
  if (!payload || !payload.exp) {
    return null;
  }
  
  // Convert Unix timestamp to Date
  return new Date(payload.exp * 1000);
}

/**
 * Validate JWT token structure (without signature verification)
 * 
 * @param token - JWT token string
 * @returns True if token has valid structure, false otherwise
 */
export function isValidJWTStructure(token: string): boolean {
  if (!token || typeof token !== 'string') {
    return false;
  }
  
  const parts = token.split('.');
  if (parts.length !== 3) {
    return false;
  }
  
  // Try to decode payload
  const payload = safeDecodeJWT(token);
  return payload !== null;
}

/**
 * Extract all claims from JWT token
 * 
 * @param token - JWT token string
 * @returns Object with all token claims or empty object if invalid
 */
export function getAllJWTClaims(token: string): Record<string, any> {
  const payload = safeDecodeJWT(token);
  return payload || {};
}

/**
 * Create a mock JWT token for development/testing
 * 
 * @param payload - Token payload object
 * @returns Base64 encoded mock token
 */
export function createMockJWT(payload: Record<string, any>): string {
  try {
    const jsonString = JSON.stringify(payload);
    return btoa(jsonString);
  } catch (error) {
    console.error('JWT: Error creating mock token', error);
    return '';
  }
}

// Export all helpers as a service object
export const jwtHelpers = {
  safeDecodeJWT,
  getTenantFromJWT,
  getRolesFromJWT,
  getUserIdFromJWT,
  isJWTExpired,
  getJWTExpiration,
  isValidJWTStructure,
  getAllJWTClaims,
  createMockJWT
};

export default jwtHelpers;
